<script setup>
import { computed } from 'vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
// GEINTEC: bg color configurable
import { getContrastingTextColor } from '@chatwoot/utils';

const props = defineProps({
  config: {
    type: Object,
    default: () => ({}),
  },
});

const { formatMessage } = useMessageFormatter();

const isDefaultScreen = computed(() => {
  return (
    props.config.isDefaultScreen &&
    ((props.config.welcomeHeading &&
      props.config.welcomeHeading.length !== 0) ||
      (props.config.welcomeTagLine && props.config.welcomeTagline.length !== 0))
  );
});

// GEINTEC: bg color configurable — mirrors the gradient/contrast logic in
// the real widget's ChatHeaderExpanded.vue (solid through the welcome text,
// then fading to fully transparent) so the preview matches.
const backgroundStyle = computed(() => {
  if (!props.config.bgColor) {
    return null;
  }
  return {
    background: `linear-gradient(180deg, ${props.config.bgColor}e6 0%, ${props.config.bgColor}e6 45%, ${props.config.bgColor}00 100%)`,
  };
});

const textColorStyle = computed(() => {
  if (!props.config.bgColor) {
    return null;
  }
  return {
    '--header-text-color': getContrastingTextColor(props.config.bgColor),
  };
});
</script>

<template>
  <div
    class="rounded-t-lg flex-shrink-0 transition-[max-height] duration-300"
    :class="[
      isDefaultScreen
        ? 'bg-n-slate-2 dark:bg-n-solid-1 px-4 py-5'
        : 'bg-n-slate-2 dark:bg-n-solid-1 p-4',
      { 'has-bg-color': config.bgColor },
      { 'min-h-[220px]': config.bgColor && isDefaultScreen },
    ]"
    :style="[backgroundStyle, textColorStyle]"
  >
    <div class="relative top-px">
      <div class="flex items-center justify-start">
        <img
          v-if="config.logo"
          :src="config.logo"
          class="mr-2 rounded-full logo"
          :class="!isDefaultScreen ? 'h-8 w-8 mb-1' : 'h-12 w-12 mb-2'"
        />
        <div v-if="!isDefaultScreen">
          <div class="flex items-center justify-start gap-1">
            <span class="text-base font-medium leading-3 text-n-slate-12">
              {{ config.websiteName }}
            </span>
            <div
              v-if="config.isOnline"
              class="w-2 h-2 bg-n-teal-10 rounded-full"
            />
          </div>
          <span class="mt-1 text-xs text-n-slate-11">
            {{ config.replyTime }}
          </span>
        </div>
      </div>
      <div v-if="isDefaultScreen" class="overflow-auto max-h-60">
        <h2 class="mb-2 text-2xl break-words text-n-slate-12">
          {{ config.welcomeHeading }}
        </h2>
        <p
          v-dompurify-html="formatMessage(config.welcomeTagline)"
          class="text-sm break-words text-n-slate-11 [&_a]:!text-n-slate-11 [&_a]:underline"
        />
      </div>
    </div>
  </div>
</template>

<!-- GEINTEC: bg color configurable -->
<style scoped>
.has-bg-color .text-n-slate-12,
.has-bg-color .text-n-slate-11 {
  color: var(--header-text-color) !important;
}
</style>
